import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SectionTitle, SectionTitleSizes } from '@components/common/section-title';
import { Head } from '@components/common/head';
import { useI18NextContext } from '@components/hooks';
import { ResortList } from '@components/resort-list';
import { PageProps } from '@model/pages';
import { Country, Region } from '@model/common';
import { ComponentContainer, SectionSeparator } from '@styles/layout';
import { ResortsBreadcrumb } from '@components/breadcrumbs';
import { Footer } from '@components/footer';
import { DestinationHero } from '@components/common/hero/destination/DestinationHero';
import { ImagesResponse } from '@model/iceberg';
import { DestinationSearchParams, getDestinationSearchParams } from '@util/destination';
import { setPartialSearch } from '@state/slices/deal-finder/dealFinderSlice';
import { getAlgoliaMatchingDestinations } from '@state/slices/algolia/algoliaSelectors';

export interface ResortsPageLayoutProps extends PageProps {
  destination: Country | Region;
  hero: ImagesResponse;
}

export const ResortsPageLayout = ({ destination, hero, promoBannerTripType }: ResortsPageLayoutProps) => {
  const dispatch = useDispatch();
  const t: any = useI18NextContext();
  const { path, display } = destination;

  const algoliaDestinations = useSelector(getAlgoliaMatchingDestinations(path));

  useEffect(() => {
    if (algoliaDestinations.length > 0) {
      const defaultSearchParams = getDestinationSearchParams({
        display,
        path,
        destinations: algoliaDestinations
      } as DestinationSearchParams);
      dispatch(setPartialSearch(defaultSearchParams));
    }
  }, [algoliaDestinations]);

  return (
    <>
      <Head
        metaTitle={t('common__label--resorts-in-title', { destination: display })}
        metaDescription={t('common__label--resorts-in-description', { destination: display })}
      />
      <DestinationHero src={hero.images[0]} promoBannerTripType={promoBannerTripType} />
      <ResortsBreadcrumb destination={destination} />
      <SectionSeparator />
      <ComponentContainer>
        <SectionTitle
          size={SectionTitleSizes.LARGE}
          main
          heading={{
            label: 'common__label--resorts-in',
            vars: { destination: display }
          }}
        />
        <ResortList destination={destination} />
      </ComponentContainer>
      <SectionSeparator />
      <Footer />
    </>
  );
};
